import React from 'react';
import FundingDealStageBadge from './FundingDealStageBadge';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const FundingDealCard = ({ deal, onSelect, onStageChange, onCreateCommission, creatingCommission, saving }) => {
  if (!deal) return null;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(deal)}
      className="w-full rounded-xl border border-[var(--fg-line)] bg-white p-3 text-left shadow-sm transition hover:border-[var(--fg-accent)] dark:bg-slate-950/20"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--fg-text)]">{deal.clientName || 'Client'}</div>
          <div className="truncate text-xs text-[var(--fg-muted)]">{deal.businessName || '—'}</div>
        </div>
        <FundingDealStageBadge stage={deal.stage} />
      </div>

      <div className="mt-3 space-y-1 text-xs text-[var(--fg-muted)]">
        <div className="font-medium text-[var(--fg-text)]">{deal.lenderName}</div>
        <div>{deal.productType}</div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[var(--fg-muted)]">Requested</div>
          <div className="font-medium text-[var(--fg-text)]">{formatCurrency(deal.requestedAmount)}</div>
        </div>
        <div>
          <div className="text-[var(--fg-muted)]">Approved / Funded</div>
          <div className="font-medium text-[var(--fg-text)]">
            {formatCurrency(deal.fundedAmount ?? deal.approvedAmount)}
          </div>
        </div>
        <div>
          <div className="text-[var(--fg-muted)]">Probability</div>
          <div className="font-medium text-[var(--fg-accent)]">{deal.probability != null ? `${deal.probability}%` : '—'}</div>
        </div>
        <div>
          <div className="text-[var(--fg-muted)]">Expected</div>
          <div className="font-medium text-[var(--fg-text)]">{formatCurrency(deal.expectedCommission)}</div>
        </div>
      </div>

      {deal.nextTask ? (
        <div className="mt-3 rounded-lg bg-[var(--fg-panel-soft)] px-2.5 py-2 text-xs">
          <div className="font-medium text-[var(--fg-text)]">{deal.nextTask}</div>
          <div className="text-[var(--fg-muted)]">Due {formatDate(deal.nextTaskDueAt)}</div>
        </div>
      ) : null}

      {onStageChange ? (
        <div className="mt-3 flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          {['Approved', 'Funded', 'Declined'].map((stage) => (
            <button
              key={stage}
              type="button"
              disabled={saving || deal.stage === stage}
              onClick={() => onStageChange(deal, stage)}
              className="rounded-md px-2 py-0.5 text-[10px] text-[var(--fg-muted)] hover:bg-[var(--fg-panel-soft)] disabled:opacity-40"
            >
              {stage}
            </button>
          ))}
          {deal.stage === 'Funded' && onCreateCommission ? (
            <button
              type="button"
              disabled={creatingCommission}
              onClick={() => onCreateCommission(deal)}
              className="rounded-md px-2 py-0.5 text-[10px] text-[var(--fg-accent)] hover:bg-[var(--fg-accent-soft)] disabled:opacity-40"
            >
              {creatingCommission ? 'Creating...' : 'Commission'}
            </button>
          ) : null}
        </div>
      ) : null}
    </button>
  );
};

export default FundingDealCard;
